import {
  Button,
  Col,
  Dropdown,
  Empty,
  Flex,
  List,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
} from "antd";
import { HelpCircle, Landmark, PlusCircle, Trash2 } from "lucide-react";
import { Exam, ExamGroup, ExamPeriod, ExamPeriodState, ExamQuestionType, Question } from "@types";
import { examQuestionsTable, newBankPoolItems } from "@configurations";
import { useCallback, useEffect, useState } from "react";
import { MenuProps } from "antd/lib";
import {
  BankPoolForm,
  ExamQuestionGroupForm,
  ExamQuestionGroupItemList,
  BankPoolTable,
} from "@components";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
} from "@dnd-kit/core";

import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import { useGetExamGroups, useGetExamGroupQuestions, useDeleteExamGroupQuestions, useExamGroupReorder, useAddExamGroupQuestion } from "@hooks";
import { motion } from "framer-motion";
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useQuestionsReorder } from "../hooks/exam/group/useQuestionsReorder";

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}
const RowTable: React.FC<Readonly<RowProps>> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'grab',
    ...(isDragging ? { position: 'relative', zIndex: 0 } : {}),
  };

  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};
interface Props {
  setActiveKey: React.Dispatch<React.SetStateAction<string>>;
  exam: Exam;
  examPeriod?: ExamPeriod;
  examPeriodScreen?: boolean;
}

export const CommonExamQuestionsTab = ({ setActiveKey, exam, examPeriod, examPeriodScreen }: Props) => {
  const examGroups = useGetExamGroups(examPeriodScreen ? examPeriod?.id : exam.id, examPeriodScreen);
  const [selectedGroup, setSelectedGroup] = useState<ExamGroup>();
  const { data: examGroupQuestionsData, isLoading: examGroupQuestionLoading } =
    useGetExamGroupQuestions(selectedGroup?.id);
  const [dataSource, setDataSource] = useState<any[]>(examGroupQuestionsData?.items || []);

  const addExamGroupQuestion = useAddExamGroupQuestion();

  const deleteExamQuestions = useDeleteExamGroupQuestions(selectedGroup?.id);
  const examGroupReorder = useExamGroupReorder(examPeriodScreen ? examPeriod?.id : exam.id, examPeriodScreen);
  const examQuestionsReorder = useQuestionsReorder(selectedGroup?.id);

  const [dataList, setDataList] = useState(examGroups.data || []);
  const [activeId, setActiveId] = useState<UniqueIdentifier>();

  const [isModalOpenQuestions, setIsModalOpenQuestions] = useState(false);
  const [isModalOpenGroups, setIsModalOpenGroups] = useState(false);
  const [isModalOpenBankPool, setIsModalOpenBankPool] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const { t } = useTranslation();

  const [questionType, setQuestionType] = useState<ExamQuestionType>();
  const sensors = useSensor(PointerSensor, {
    activationConstraint: { distance: 1 },
  })

  const onClick: MenuProps["onClick"] = ({ key }: any) => {
    setIsModalOpenQuestions(true);
    setQuestionType(key);
  };

  useEffect(() => {
    if (examGroups.data) {
      setDataList(examGroups.data.sort((a, b) => a.orderNumber - b.orderNumber));
    }
    if (examGroupQuestionsData) {
      setDataSource(examGroupQuestionsData.items);
    }
  }, [examGroups.data, examGroupQuestionsData]);

  useEffect(() => {
    if (examGroupQuestionsData) {
      setDataSource(examGroupQuestionsData.items.sort((a: any, b: any) => a.orderNumber - b.orderNumber));
    }
  }, [examGroupQuestionsData])

  // Auto-select first exam group when data loads
  useEffect(() => {
    if (dataList.length > 0 && !selectedGroup) {
      setSelectedGroup(dataList[0]);
    }
  }, [dataList]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataList((prev) => {
        const activeIndex = prev.findIndex((i) => i.id === active.id);
        const overIndex = prev.findIndex((i) => i.id === over?.id);
        const arr = arrayMove(prev, activeIndex, overIndex);
        arr.forEach((item, index) => {
          item.orderNumber = index;
        });
        examGroupReorder.mutate(arr);
        return arr;
      });

    }
    setActiveId(undefined)
  };

  const onDragEndQuestions = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prev) => {
        const activeIndex = prev.findIndex((i) => i.id === active.id);
        const overIndex = prev.findIndex((i) => i.id === over?.id);
        const arr = arrayMove(prev, activeIndex, overIndex);
        arr.forEach((item, index) => {
          item.orderNumber = index;
        });
        examQuestionsReorder.mutate(arr)
        return arr;
      });
    }
  };

  const setDisabledButtons = (): boolean => {

    if (!examPeriod) return false;

    if (examPeriod.state !== ExamPeriodState.InActive) {
      return true;
    }
    if (examPeriodScreen) return true;

    return false;
  }

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xl={8} lg={8} md={24} sm={24} xs={24}>
        <Flex justify="space-between">
          <Typography.Title level={4} style={{ margin: 0, marginBottom: 12 }}>
            {t("pages.examsManagment.examQuestionsTab.group")}
          </Typography.Title>
          <Button
            id="new-group-button"
            type="primary"
            disabled={setDisabledButtons()}
            icon={<PlusCircle />}
            onClick={() => setIsModalOpenGroups(true)}
          >
            {t("pages.examsManagment.examQuestionsTab.newGroupButton")}
          </Button>
        </Flex>
        <div
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            padding: '8px',
          }}
        >
          <DndContext
            onDragStart={handleDragStart}
            onDragEnd={onDragEnd}
            sensors={[
              sensors,
            ]}
          >
            <SortableContext
              items={dataList.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            // disabled={examPeriod?.state !== ExamPeriodState.InActive}
            >
              <List
                dataSource={dataList}
                loading={examGroups.isLoading}
                rowKey={(record) => record.id}
                renderItem={(item, index) => {
                  return (
                    <ExamQuestionGroupItemList
                      examPeriodScreen={examPeriodScreen}
                      index={index}
                      activeId={activeId}
                      selectedGroup={selectedGroup}
                      onClick={() => {
                        setSelectedRowKeys([]);
                        setSelectedGroup(item);
                      }}

                      questionGroup={item}
                    />
                  );
                }}
              />
            </SortableContext>
            <DragOverlay adjustScale>
              {activeId ? (
                <ExamQuestionGroupItemList
                  isDragging
                  index={dataList.filter((item) => item.id === activeId)[0]?.orderNumber || 0}
                  questionGroup={
                    dataList.filter((item) => item.id === activeId)[0]!
                  }
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </Col>
      <Col xl={16} lg={16} md={24} sm={24} xs={24}>
        {selectedGroup ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            key={selectedGroup.id}
            style={{
              boxShadow: "rgba(0, 0, 0, 0.16) 1px 0px 5px",
              borderRadius: "8px",
            }}
          >
            <Flex style={{ padding: 16 }} justify="space-between">
              <Typography.Title level={5} style={{ margin: 0 }}>
                {selectedGroup?.name}
              </Typography.Title>
              <Space>
                <Dropdown
                  menu={{ items: newBankPoolItems(exam?.isP365Exam), onClick: onClick }}
                  placement="bottomLeft"
                  disabled={setDisabledButtons()}
                  arrow
                >
                  <Button
                    type="primary"

                    icon={<PlusCircle />}
                    loading={addExamGroupQuestion.isPending}
                  >
                    {t(
                      "pages.examsManagment.examQuestionsTab.newQuestionButton"
                    )}
                  </Button>
                </Dropdown>
                <Button
                  id="bank-pool-button"
                  type="dashed"
                  disabled={setDisabledButtons()}
                  onClick={() => setIsModalOpenBankPool(true)}
                  icon={<Landmark />}
                >
                  {t("pages.examsManagment.examQuestionsTab.addQuestionBank")}
                </Button>
                <Popconfirm
                  title={t(
                    "pages.examsManagment.examQuestionsTab.deleteQuestion"
                  )}
                  description={t(
                    "pages.examsManagment.examQuestionsTab.deleteQuestionDesc"
                  )}
                  onConfirm={() =>
                    deleteExamQuestions.mutate(
                      dataSource.filter((q: any) => selectedRowKeys.includes(q.id)),
                      {
                        onSuccess: () => {
                          setSelectedRowKeys([]);
                        },
                      }
                    )
                  }
                  okButtonProps={{ loading: deleteExamQuestions.isPending }}
                  icon={<HelpCircle style={{ color: "red", marginRight: 8 }} />}
                >
                  <Button
                    danger
                    type="primary"
                    icon={<Trash2 />}
                    disabled={selectedRowKeys.length == 0}
                  />
                </Popconfirm>
              </Space>
            </Flex>
            <DndContext id="questions" onDragEnd={onDragEndQuestions} modifiers={[restrictToVerticalAxis]} sensors={[sensors]}>
              <SortableContext
                // rowKey array
                // disabled={examPeriod?.state !== ExamPeriodState.InActive}
                items={dataSource.map((i: any) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <Table
                  rowKey={(record) => record.id}
                  rowSelection={examPeriodScreen ? undefined : {
                    type: !examPeriodScreen ? "checkbox" : undefined,
                    onChange: !examPeriodScreen ? onSelectChange : undefined,
                  }}
                  components={{
                    body: { row: RowTable },
                  }}
                  size="small"
                  pagination={{
                    showTotal: (total) =>
                      `${t(
                        "pages.examsManagment.examQuestionsTab.totalQuestions"
                      )}: ${total}`,
                  }}
                  dataSource={dataSource}
                  loading={examGroupQuestionLoading}
                  columns={examQuestionsTable(selectedGroup?.id, examPeriodScreen)}
                />
              </SortableContext>
            </DndContext>
          </motion.div>
        ) : (
          <Empty
            key={"empty"}
            description={t("pages.examsManagment.examQuestionsTab.empty")}
          />
        )}
      </Col>

      <Col
        span={24}
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 0,
        }}
      >
        <Space>
          <Button>{t("pages.examsManagment.cancel")}</Button>
          <Button type="primary" onClick={() => setActiveKey("3")}>
            {t("pages.examsManagment.next")}
          </Button>
        </Space>
      </Col>
      <Modal
        title={t("pages.bankPool.newQuestion")}
        footer={null}
        maskClosable={false}
        onCancel={() => setIsModalOpenQuestions(false)}
        destroyOnClose
        styles={{
          body: {
            paddingRight: 8,
            paddingLeft: 4,
            height: "calc(80vh - 30px)",
            overflowY: "auto"
          },
        }}
        open={isModalOpenQuestions}
      >
        <BankPoolForm
          questionCreated={(question: Question) => {
            const nextOrderNumber =
              dataSource.length > 0
                ? Math.max(...dataSource.map((q) => q.orderNumber)) + 1
                : 1;

            addExamGroupQuestion.mutate({
              groupId: selectedGroup!.id,
              questionsOrder: [{ questionId: question.id, orderNumber: nextOrderNumber }],
            });
          }}
          examQuestionType={questionType!}
          setIsModalOpen={setIsModalOpenQuestions}
        />
      </Modal>

      <Modal
        title={t("pages.examsManagment.examQuestionsTab.newGroupButton")}
        footer={null}
        onCancel={() => setIsModalOpenGroups(false)}
        destroyOnClose
        open={isModalOpenGroups}
      >
        <ExamQuestionGroupForm
          setIsModalOpen={setIsModalOpenGroups}
          examGroupLength={examGroups.data?.length}
          exam={exam}
        />
      </Modal>

      <Modal
        title={t("bankPool")}
        width={1000}
        footer={null}
        onCancel={() => setIsModalOpenBankPool(false)}
        destroyOnClose
        open={isModalOpenBankPool}
      >
        <BankPoolTable
          setIsModalOpen={setIsModalOpenBankPool}
          group={selectedGroup!}
          exam={exam}
        />
      </Modal>
    </Row>
  );
};
